import React from 'react';
import { Pressable, DimensionValue } from 'react-native';
import {
  HStack, Box, Text, Icon,
} from '@gluestack-ui/themed';
import { ChevronUp, ChevronDown } from 'lucide-react-native';
import type { SortingState } from '@tanstack/react-table';

interface EvaluationTableHeaderProps {
  sorting: SortingState;
  onSortChange: (columnId: string) => void;
}

interface ColumnHeaderProps {
  label: string;
  columnId: string;
  width?: DimensionValue;
  flex?: number;
  sortable?: boolean;
  sorting: SortingState;
  onSort: (columnId: string) => void;
}

const ColumnHeader: React.FC<ColumnHeaderProps> = ({
  label,
  columnId,
  width,
  flex,
  sortable = true,
  sorting,
  onSort,
}) => {
  const currentSort = sorting.find((s) => s.id === columnId);
  const isSorted = !!currentSort;
  const isDesc = currentSort?.desc ?? false;

  const handlePress = () => {
    if (sortable) {
      onSort(columnId);
    }
  };

  const content = (
    <HStack alignItems="center" space="xs">
      <Text
        size="xs"
        fontWeight="bold"
        color={isSorted ? '$brandBase' : '$textSubtle'}
        textTransform="uppercase"
      >
        {label}
      </Text>
      {sortable && (
        <Box opacity={isSorted ? 1 : 0.3}>
          <Icon
            as={isDesc ? ChevronDown : ChevronUp}
            size="xs"
            color={isSorted ? '$brandBase' : '$textSubtle'}
          />
        </Box>
      )}
    </HStack>
  );

  const boxProps = {
    ...(width ? { w: width } : {}),
    ...(flex ? { flex } : {}),
  };

  if (!sortable) {
    return <Box {...boxProps} py="$1">{content}</Box>;
  }

  const wrapperStyle = flex ? { flex: 1 } : undefined;

  return (
    <Pressable onPress={handlePress} style={wrapperStyle}>
      <Box {...boxProps} py="$1">
        {content}
      </Box>
    </Pressable>
  );
};

/**
 * Enhanced table header for evaluations with sortable columns.
 * Displays sort indicators and handles sort state changes.
 */
export const EvaluationTableHeader: React.FC<EvaluationTableHeaderProps> = ({
  sorting,
  onSortChange,
}) => (
  <HStack
    px="$6"
    py="$3.5"
    bg="$bgSubtle"
    borderBottomWidth={1}
    borderBottomColor="$borderLight"
  >
    <ColumnHeader
      label="Student"
      columnId="student"
      width={300}
      sorting={sorting}
      onSort={onSortChange}
    />
    <ColumnHeader
      label="Module"
      columnId="module"
      flex={1}
      sorting={sorting}
      onSort={onSortChange}
    />
    <ColumnHeader
      label="Due Date"
      columnId="dueDate"
      width={110}
      sortable={false}
      sorting={sorting}
      onSort={onSortChange}
    />
    <ColumnHeader
      label="Submitted"
      columnId="submittedAt"
      width={110}
      sorting={sorting}
      onSort={onSortChange}
    />
    <ColumnHeader
      label="Status"
      columnId="status"
      width={100}
      sortable={false}
      sorting={sorting}
      onSort={onSortChange}
    />
    <ColumnHeader
      label="Grade"
      columnId="grade"
      width={80}
      sorting={sorting}
      onSort={onSortChange}
    />
    {/* Spacer for arrow */}
    <Box w={40} />
  </HStack>
);

export default EvaluationTableHeader;
